import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { inr, num, pct, compact } from "@/lib/format";
import { Table2, ArrowUp, ArrowDown, Download, Search, X } from "lucide-react";
import PaperExplorer from "@/pages/PaperExplorer";
import { visibleInterval } from "@/lib/poll";
import ResetDefaultsButton from "@/components/ResetDefaultsButton";

const DEFAULT_SORT = [{ k: "annualized_gap_pct", dir: "desc" }, { k: "liquidity_score", dir: "desc" }];

const COLS = [
  { k: "symbol", label: "Symbol", type: "str", sticky: true },
  { k: "spot", label: "Spot", type: "num" },
  { k: "near_price", label: "Near", type: "num" },
  { k: "near_bid", label: "N.Bid", type: "num" },
  { k: "near_ask", label: "N.Ask", type: "num" },
  { k: "near_oi", label: "N.OI", type: "compact" },
  { k: "mid_price", label: "Mid", type: "num" },
  { k: "far_price", label: "Far", type: "num" },
  { k: "far_bid", label: "F.Bid", type: "num" },
  { k: "far_ask", label: "F.Ask", type: "num" },
  { k: "far_oi", label: "F.OI", type: "compact" },
  { k: "lot_size", label: "Lot", type: "int" },
  { k: "contract_value", label: "Contract ₹", type: "money" },
  { k: "spot_near_gap_pct", label: "Spot→Near %", type: "pct" },
  { k: "near_mid_gap_abs", label: "N→M ₹", type: "num" },
  { k: "near_mid_gap_pct", label: "N→M %", type: "pct" },
  { k: "mid_far_gap_abs", label: "M→F ₹", type: "num" },
  { k: "mid_far_gap_pct", label: "M→F %", type: "pct" },
  { k: "near_far_gap_abs", label: "N→F ₹", type: "num" },
  { k: "near_far_gap_pct", label: "N→F %", type: "pct" },
  { k: "annualized_gap_pct", label: "Annual %", type: "pct", strong: true },
  { k: "direction", label: "Structure", type: "dir" },
  { k: "hist_avg_spread", label: "Hist Avg", type: "num" },
  { k: "spread_percentile", label: "Pctile", type: "pct1" },
  { k: "spread_zscore", label: "Z-Score", type: "num" },
  { k: "executable_spread", label: "Exec Spread", type: "num", strong: true },
  { k: "bid_ask_spread", label: "Bid-Ask", type: "num" },
  { k: "est_roundtrip_charges", label: "RT Charges", type: "money" },
  { k: "approx_margin_per_lot", label: "Margin/Lot", type: "money" },
  { k: "liquidity_score", label: "Liquidity", type: "num1" },
  { k: "opportunity_score", label: "Opp.", type: "num1", strong: true },
];

function fmt(v, type) {
  switch (type) {
    case "num": return num(v, 2);
    case "num1": return num(v, 1);
    case "int": return num(v, 0);
    case "money": return inr(v);
    case "compact": return compact(v);
    case "pct": return pct(v, 2);
    case "pct1": return v == null ? "—" : num(v, 1);
    default: return v ?? "—";
  }
}

export default function Explorer() {
  const { environment } = useApp();
  const [rows, setRows] = useState([]);
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [q, setQ] = useState("");
  const [structure, setStructure] = useState("all");
  const [minOI, setMinOI] = useState(0);
  const [minLiq, setMinLiq] = useState(0);
  const [minGap, setMinGap] = useState("");
  const [maxGap, setMaxGap] = useState("");
  const [maxMargin, setMaxMargin] = useState("");
  const [excludeBan, setExcludeBan] = useState(false);
  const prev = useRef({});
  const nav = useNavigate();

  useEffect(() => {
    if (environment !== "SIMULATION") return;
    const load = () => api.get("/explorer").then((r) => {
      const next = {};
      r.data.rows.forEach((row) => { next[row.symbol] = row.near_price; });
      prev.current = { data: next, old: prev.current.data || {} };
      setRows(r.data.rows);
    }).catch(() => {});
    load();
    return visibleInterval(load, 4000);
  }, [environment]);

  const toggleSort = (k) => {
    setSort((cur) => {
      const existing = cur.find((s) => s.k === k);
      if (!existing) return [{ k, dir: "desc" }, ...cur.filter((s) => s.k !== k)].slice(0, 3);
      if (existing.dir === "desc") return cur.map((s) => (s.k === k ? { k, dir: "asc" } : s));
      return cur.filter((s) => s.k !== k); // clear
    });
  };

  const filtered = useMemo(() => {
    let r = rows.filter((row) => {
      if (q && !(row.symbol.includes(q.toUpperCase()) || row.name.toUpperCase().includes(q.toUpperCase()))) return false;
      if (structure !== "all" && row.direction !== structure) return false;
      if (minOI && row.near_oi < minOI) return false;
      if (minLiq && row.liquidity_score < minLiq) return false;
      if (minGap !== "" && row.near_far_gap_pct < parseFloat(minGap)) return false;
      if (maxGap !== "" && row.near_far_gap_pct > parseFloat(maxGap)) return false;
      if (maxMargin !== "" && row.approx_margin_per_lot > parseFloat(maxMargin)) return false;
      if (excludeBan && row.fno_ban) return false;
      return true;
    });
    r = [...r].sort((a, b) => {
      for (const s of sort) {
        const av = a[s.k], bv = b[s.k];
        if (av === bv) continue;
        if (av == null) return 1; if (bv == null) return -1;
        const cmp = typeof av === "string" ? av.localeCompare(bv) : av - bv;
        return s.dir === "asc" ? cmp : -cmp;
      }
      return 0;
    });
    return r;
  }, [rows, q, structure, minOI, minLiq, minGap, maxGap, maxMargin, excludeBan, sort]);

  const exportCSV = () => {
    const header = COLS.map((c) => c.label).join(",");
    const lines = filtered.map((row) => COLS.map((c) => row[c.k]).join(","));
    const blob = new Blob([[header, ...lines].join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "nse_futures_explorer.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  if (environment === "PAPER") return <PaperExplorer />;

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-xl font-bold tracking-tight flex items-center gap-2"><Table2 className="h-5 w-5 text-[#2563EB]" /> Futures Explorer
          <span className="text-xs text-slate-500 font-normal ml-2">{filtered.length}/{rows.length} stocks · SIMULATION (MOCK)</span>
        </h1>
        <button onClick={exportCSV} data-testid="explorer-export-csv" className="text-xs font-semibold px-3 py-1.5 rounded-sm bg-[#1A1D24] border border-[#2A2F3A] hover:bg-[#232730] flex items-center gap-1.5">
          <Download className="h-3.5 w-3.5" /> Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="term-panel rounded-sm p-3 flex flex-wrap items-center gap-2 text-xs">
        <div className="relative">
          <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" />
          <input data-testid="explorer-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search symbol"
            className="bg-[#0A0C10] border border-[#2A2F3A] rounded-sm pl-7 pr-2 py-1.5 w-40 outline-none focus:border-[#2563EB] uppercase mono" />
        </div>
        <div className="flex rounded-sm overflow-hidden border border-[#2A2F3A]">
          {["all", "contango", "backwardation"].map((s) => (
            <button key={s} data-testid={`filter-${s}`} onClick={() => setStructure(s)}
              className={`px-2.5 py-1.5 capitalize ${structure === s ? "bg-[#2563EB] text-white" : "bg-[#0A0C10] text-slate-400 hover:text-white"}`}>{s}</button>
          ))}
        </div>
        <input value={minGap} onChange={(e) => setMinGap(e.target.value)} placeholder="Min N→F %" type="number"
          className="bg-[#0A0C10] border border-[#2A2F3A] rounded-sm px-2 py-1.5 w-24 outline-none mono" data-testid="filter-min-gap" />
        <input value={maxGap} onChange={(e) => setMaxGap(e.target.value)} placeholder="Max N→F %" type="number"
          className="bg-[#0A0C10] border border-[#2A2F3A] rounded-sm px-2 py-1.5 w-24 outline-none mono" data-testid="filter-max-gap" />
        <input value={minOI || ""} onChange={(e) => setMinOI(Number(e.target.value) || 0)} placeholder="Min OI" type="number"
          className="bg-[#0A0C10] border border-[#2A2F3A] rounded-sm px-2 py-1.5 w-24 outline-none mono" data-testid="filter-min-oi" />
        <input value={minLiq || ""} onChange={(e) => setMinLiq(Number(e.target.value) || 0)} placeholder="Min Liq" type="number"
          className="bg-[#0A0C10] border border-[#2A2F3A] rounded-sm px-2 py-1.5 w-24 outline-none mono" data-testid="filter-min-liq" />
        <input value={maxMargin} onChange={(e) => setMaxMargin(e.target.value)} placeholder="Max Margin/Lot" type="number"
          className="bg-[#0A0C10] border border-[#2A2F3A] rounded-sm px-2 py-1.5 w-28 outline-none mono" data-testid="filter-max-margin" />
        <label className="flex items-center gap-1.5 cursor-pointer text-slate-400">
          <input type="checkbox" checked={excludeBan} onChange={(e) => setExcludeBan(e.target.checked)} data-testid="filter-exclude-ban" /> Exclude F&O ban
        </label>
        <ResetDefaultsButton testId="explorer-reset-defaults" className="h-[30px]"
          title="Clear every filter and rank by annualised gap, then liquidity"
          onReset={() => { setQ(""); setStructure("all"); setMinOI(0); setMinLiq(0); setMinGap(""); setMaxGap(""); setMaxMargin(""); setExcludeBan(false); setSort(DEFAULT_SORT); }} />
        {sort.length > 0 && (
          <button onClick={() => setSort([])} className="ml-auto text-slate-500 hover:text-white flex items-center gap-1"><X className="h-3 w-3" /> clear sort</button>
        )}
      </div>

      {/* Table */}
      <div className="term-panel rounded-sm overflow-auto" style={{ maxHeight: "calc(100vh - 220px)" }}>
        <table className="w-full text-xs border-collapse">
          <thead className="sticky top-0 z-10">
            <tr className="bg-[#1A1D24]">
              {COLS.map((c) => {
                const s = sort.find((x) => x.k === c.k);
                const idx = sort.findIndex((x) => x.k === c.k);
                return (
                  <th key={c.k} onClick={() => toggleSort(c.k)} data-testid={`col-${c.k}`}
                    className={`px-2 py-2 whitespace-nowrap cursor-pointer select-none border-b border-[#2A2F3A] font-semibold ${c.type === "str" || c.type === "dir" ? "text-left" : "text-right"} ${c.sticky ? "sticky left-0 bg-[#1A1D24] z-20" : ""} ${c.strong ? "text-slate-100" : "text-slate-400"} hover:text-white`}>
                    <span className="inline-flex items-center gap-1">
                      {c.label}
                      {s && (s.dir === "asc" ? <ArrowUp className="h-3 w-3 text-[#2563EB]" /> : <ArrowDown className="h-3 w-3 text-[#2563EB]" />)}
                      {s && sort.length > 1 && <span className="text-[9px] text-[#2563EB]">{idx + 1}</span>}
                    </span>
                  </th>
                );
              })}
              <th className="px-2 py-2 border-b border-[#2A2F3A] bg-[#1A1D24]" />
            </tr>
          </thead>
          <tbody>
            {filtered.map((row) => {
              const oldP = prev.current.old?.[row.symbol];
              const flash = oldP != null && oldP !== row.near_price ? (row.near_price > oldP ? "flash-up" : "flash-down") : "";
              return (
                <tr key={row.symbol} className=" gz-row">
                  {COLS.map((c) => {
                    const v = row[c.k];
                    let color = "text-slate-300";
                    if (c.type === "pct" && v != null) color = v > 0 ? "text-[#00C805]" : v < 0 ? "text-[#FF3B30]" : "text-slate-400";
                    if (c.k === "spread_zscore" && v != null) color = Math.abs(v) > 1.5 ? "text-[#FFCC00]" : "text-slate-300";
                    return (
                      <td key={c.k} className={`px-2 py-1.5 whitespace-nowrap mono ${c.type === "str" || c.type === "dir" ? "text-left" : "text-right"} ${c.sticky ? `sticky left-0 bg-[#12151C] font-bold text-[var(--term-primary)] ${flash}` : color} ${c.strong ? "font-semibold" : ""}`}>
                        {c.type === "dir" ? (
                          <span className={`px-1.5 py-0.5 rounded-sm text-[10px] font-bold ${v === "contango" ? "bg-[#2563EB]/15 text-[#5b9bff]" : "bg-[#FFCC00]/15 text-[#FFCC00]"}`}>
                            {v === "contango" ? "↑ CONTANGO" : "↓ BACKWDN"}
                          </span>
                        ) : c.sticky ? (
                          <div className="flex items-center gap-1.5">{v}{row.fno_ban && <span className="text-[8px] px-1 rounded-sm bg-[#FF3B30]/20 text-[#FF6b62]">BAN</span>}</div>
                        ) : fmt(v, c.type)}
                      </td>
                    );
                  })}
                  <td className="px-2 py-1.5 bg-[#12151C]">
                    <button onClick={() => nav(`/builder?symbol=${row.symbol}`)} data-testid={`trade-${row.symbol}`}
                      className="text-[10px] font-bold px-2 py-1 rounded-sm bg-[#2563EB]/20 text-[#5b9bff] hover:bg-[#2563EB]/40 whitespace-nowrap">TRADE</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="text-[10px] text-slate-600">Displayed LTP gaps are for scanning only. Live order decisions use executable bid/ask spread. Historical stats are statistical, not predictions.</div>
    </div>
  );
}
